import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { formatPlural } from '../components/Formatted';
import { AWARD_POINTS, AWARD_SCORES } from '../constants';
import { resolveInlineLink } from './location';

const mapping = { [AWARD_POINTS]: 'Point', [AWARD_SCORES]: 'Score' };
export type TypeAwards = typeof AWARD_POINTS | typeof AWARD_SCORES;

const stringifyPoints = (points: number) => {
  return parseFloat(points.toFixed(1)).toString();
};

export function transformScores(type: TypeAwards = AWARD_POINTS, text: string, points: number, maxPoints: number) {
  const pointsString = stringifyPoints(points);
  const maxPointsString = stringifyPoints(maxPoints);

  let result = text.replace(/\$колво/g, pointsString).replace(/\$макс/g, maxPointsString);

  if (mapping[type]) {
    const label = formatPlural({ value: points, index: mapping[type] });
    result = result.replace(/\$текст/g, `${pointsString} ${label}`);
  }
  return result;
}

/**
 * Санирует Markdown-форматирование, вынося специальные символы или пробелы
 * за пределы форматированных блоков (жирного или курсивного текста).
 *
 * Пример:
 * - Вход: `**Пример?**`
 * - Результат: `**Пример**?`
 */
function sanitizeMarkdownFormatting(text: string): string {
  const markdownFormattingSanitizerRegex = /(\*\*|\*)([^\n*]+?)([!?.,])(\*\*|\*)/g;

  function process(text: string): string {
    if (!markdownFormattingSanitizerRegex.test(text)) {
      return text;
    }
    return text.replace(markdownFormattingSanitizerRegex, (_, open, content, symbol, close) => {
      const sanitizedContent = process(content.trim());
      return `${open}${sanitizedContent}${close}${symbol}`;
    });
  }

  return process(text);
}

const escapes = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
const backEscapes = Object.entries(escapes).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {});

export function replaceEscaped(value: string) {
  return value.replace(/&(amp|lt|gt|quot);/g, tag => backEscapes[tag] || tag);
}

const renderer = new marked.Renderer();

renderer.link = function (href, title, text) {
  const resolvedHref = resolveInlineLink(href ?? '');
  const link = marked.Renderer.prototype.link.apply(this, [resolvedHref ?? null, title, text]);
  return link.replace('<a', '<a target="_blank" rel="nofollow noopener"');
};

renderer.code = function (code, lang, isEscaped) {
  code = replaceEscaped(code);
  code = marked.Renderer.prototype.code.apply(this, [code, lang, isEscaped]);

  return code.replace(/<pre>/, `<pre class="language-${lang}">`);
};

renderer.listitem = function (item, task, checked) {
  const listitem = marked.Renderer.prototype.listitem.apply(this, [item, task, checked]);

  return listitem.replace(/<p>(.*?)<\/p>/g, `$1`);
};

renderer.blockquote = function (quote) {
  return marked.Renderer.prototype.blockquote.apply(this, [quote]);
};

renderer.hr = function () {
  return marked.Renderer.prototype.hr.apply(this);
};

const ALLOWED_TAGS = [
  'p',
  'a',
  'ul',
  'ol',
  'li',
  'b',
  'i',
  'strong',
  'em',
  'br',
  'span',
  'pre',
  'code',
  'blockquote',
  'hr',
];
const ALLOWED_ATTR = ['href', 'rel', 'target', 'class', 'title', 'data-tooltip'];

function processTooltip(text: string): string {
  const processedText = marked(text, { renderer }).trim();
  return processedText.replace(/^<p>|<\/p>$/g, '');
}

function textMarked(text: string, options?: marked.MarkedOptions) {
  const codeBlockRegEx = /(<br\/>)?```.*?```(<br\/>)?/gm;
  let correctText = text
    .replace(/<br\/>-/g, ' \n-')
    .replace(/<br\/>[1-9]?[0-9]\. /g, n => ` \n${n.slice(5)}`)
    .replace(codeBlockRegEx, match => {
      return match.replace(/<br\/>/g, '\n');
    });

  correctText = sanitizeMarkdownFormatting(correctText);
  // Обрабатываем шаблон {{текст|подсказка}}
  correctText = correctText.replaceAll(/{{(.*?)\|(.*?)}}/gs, (_, text: string, title: string) => {
    const sanitizedTitle = processTooltip(title.replace(/\\([^nrtbf])/g, '$1')).replace(/"/g, '&quot;');
    return `<span data-tooltip="${sanitizedTitle}" title="${sanitizedTitle}">${text}</span>`;
  });

  correctText = marked(correctText, { renderer, ...(options ? options : {}) });
  // Удаляет лишние отступы, которые были необходимы для marked
  correctText = correctText.replace(/\n<pre/gm, '<pre').replace(/<\/pre>\n/gm, '</pre>');

  return correctText;
}

export function textHtml(text: string, options?: marked.MarkedOptions) {
  const purify = DOMPurify();

  purify.addHook('uponSanitizeAttribute', (node, hookEvent) => {
    const href = node.getAttribute('href') ?? '';

    if (node.tagName === 'A' && href.startsWith('tinkoffbank://')) {
      hookEvent.forceKeepAttr = true;
    }
  });

  return purify.sanitize(textMarked(text, options), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}
